/**
 * Demo DB の型定義のみを持つモジュール。
 *
 * 元は `schema.ts` に DemoDatabase クラス（Dexie + EntityTable<T> × 25 + migration version × 16）
 * と全ての型定義（700 行超）が同居していたため、route -api / -query / components が
 * `import type { Foo } from "@/mocks/db/schema"` するだけで Dexie generic Table 型 + 全 EntityTable
 * の instantiation を tsgo に評価させ、型相関 Map を圧迫していた。
 *
 * このモジュールは型のみを export し、DemoDatabase クラスは見えない。
 * 値（`db`）が必要な mock repository 層だけが `schema.ts` を import する設計に寄せる。
 */

import type { StructureCellRole } from '@/features/engine-shared/structure-detail/types';
import type { OperationEventSnapshotRow } from './operationEventPayload';

// ─── Existing ───────────────────────────────────────────────

export interface Person {
  id: string;
  name: string;
  department: string;
}

// ─── Schema-local row type (decoupled from feature UI types) ─

export interface SchemaTableColumn {
  largeCategoryCode: string;
  largeCategoryName: string;
  mediumCategoryCode: string;
  mediumCategoryName: string;
  inputFieldCount: number;
  requiredFieldCount: number;
  hasIncludeFile: boolean;
}
export interface SchemaTableTarget {
  targetType: string;
  targetId: string;
  targetName: string;
  targetAvatarUrl?: string;
}
export interface SchemaTableRow {
  id: string;
  completedOperationStepCount: number;
  totalOperationStepCount: number;
  target: SchemaTableTarget;
  anchorDate: string;
  columns: SchemaTableColumn[];
  /** 任意の属性テキスト列（AggregateTable の attributeColumns で表示）。 */
  attributes?: Record<string, string>;
}

// ─── Attendance Domain ──────────────────────────────────────

export type AttendanceRecordStatus = 'present' | 'absent' | 'late' | 'early_leave' | 'holiday' | 'paid_leave' | 'sick_leave' | 'non_working_day';
export type AttendanceApprovalStatus = 'pending' | 'approved' | 'rejected';
export type ClockingRecordType = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
export type ClockingRecordMethod = 'web' | 'mobile' | 'ic_card' | 'biometric' | 'manual';
export type AttendanceAlertType = 'overtime_limit' | 'no_clock_in' | 'no_clock_out' | 'consecutive_overtime' | 'interval_violation' | 'monthly_80h_exceeded' | 'annual_360h_exceeded';
export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AttendanceEmployeeStatus = 'active' | 'on_leave' | 'retired';
export interface AttendanceRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  date: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart: string | null;
  actualEnd: string | null;
  status: AttendanceRecordStatus;
  overtimeMinutes: number;
  lateMinutes: number;
  memo: string;
  workplaceName: string;
  approvalStatus: AttendanceApprovalStatus;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}
export type TimeClockCorrectionStatus = 'pending' | 'approved' | 'rejected';

/**
 * 打刻の修正（だこくのしゅうせい / Time Clock Correction）の一括申請レコード。
 * 従業員が日次勤怠の出勤・退勤・休憩を修正し、人事承認のために申請したもの（デモ用 Dexie のみ）。
 */
export interface TimeClockCorrection {
  id: string;
  employeeId: string;
  organizationId: string;
  /** 対象日 YYYY-MM-DD */
  date: string;
  /** 申請単位（同一一括申請でまとめる） */
  batchId: string;
  originalClockIn: string | null;
  originalClockOut: string | null;
  originalBreakMinutes: number;
  correctedClockIn: string | null;
  correctedClockOut: string | null;
  correctedBreakMinutes: number;
  reason: string;
  status: TimeClockCorrectionStatus;
  createdAt: string;
}
export interface ClockingRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  timestamp: string;
  type: ClockingRecordType;
  method: ClockingRecordMethod;
  locationName: string | null;
  latitude: number | null;
  longitude: number | null;
  deviceId: string | null;
  verified: boolean;
  organizationId: string;
  createdAt: string;
}
export interface AttendanceAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  type: AttendanceAlertType;
  severity: AlertSeverity;
  message: string;
  date: string;
  resolved: boolean;
  resolvedAt: string | null;
  organizationId: string;
  createdAt: string;
}
export interface AttendanceEmployee {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  attendanceStructureId: string;
  attendanceStructureName: string;
  attendanceManagerId: string | null;
  attendanceManagerName: string | null;
  monthlyWorkDays: number;
  monthlyOvertimeHours: number;
  paidLeaveRemaining: number;
  paidLeaveTotal: number;
  status: AttendanceEmployeeStatus;
  organizationId: string;
}

// ─── Payroll Domain ─────────────────────────────────────────

export type PaySlipStatus = 'draft' | 'calculating' | 'pending_approval' | 'approved' | 'paid' | 'cancelled';
export type PaySlipRecordCategory = 'earning' | 'deduction' | 'tax' | 'social_insurance' | 'net_pay';
export interface PaySlip {
  id: string;
  title: string;
  period: string;
  paymentDate: string;
  status: PaySlipStatus;
  employeeCount: number;
  totalGrossAmount: number;
  totalNetAmount: number;
  totalDeductions: number;
  currency: string;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface PaySlipRecord {
  id: string;
  paySlipId: string;
  employeeId: string;
  employeeName: string;
  department: string;
  category: PaySlipRecordCategory;
  itemName: string;
  itemCode: string;
  amount: number;
  currency: string;
  description: string;
  sortOrder: number;
  createdAt: string;
}
export interface PaySlipFolder {
  id: string;
  paySlipId: string;
  name: string;
  description: string;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface PaySlipFile {
  id: string;
  paySlipId: string;
  folderId: string;
  name: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedBy: string;
  createdAt: string;
}
export interface EmployeePaySlipSummary {
  id: string;
  employeeId: string;
  employeeName: string;
  email: string;
  avatarUrl: string | null;
  organization: string;
  period: string;
  grossAmount: number;
  prevGrossAmount: number | null;
  totalDeductions: number;
  prevTotalDeductions: number | null;
  netAmount: number;
  prevNetAmount: number | null;
  overtimeHours: string;
  operationActivity: string | null;
  organizationId: string;
}
export interface EmployeePayslipAttendance {
  id: string;
  employeeId: string;
  period: string;
  organizationId: string;
  scheduledWorkDays: number;
  attendanceDays: number;
  absenceDays: number;
  holidayWorkDays: number;
  paidLeaveUsedDays: number;
  paidLeaveRemainingDays: number;
  workHours: number;
  overtimeHours: number;
  nightOvertimeHours: number;
  holidayWorkHours: number;
  lateEarlyHours: number;
}
export type PaySlipCalculationCategoryId = 'attendance' | 'earnings' | 'social_insurance' | 'tax' | 'result';
export interface PaySlipPayItemLine {
  lineKey: string;
  label: string;
  valueText: string;
  valueUnit?: string | null;
}
export interface PaySlipCalculationSnapshotRow {
  categoryId: PaySlipCalculationCategoryId;
  variableKey: string;
  label: string;
  valueText: string;
  valueUnit?: string | null;
  lines?: PaySlipPayItemLine[] | null;
}
export interface PaySlipCalculationSnapshot {
  id: string;
  employeeId: string;
  period: string;
  snapshotAt: string;
  title: string;
  rows: PaySlipCalculationSnapshotRow[];
  organizationId: string;
}

// ─── Engine Domain ──────────────────────────────────────────

export type EngineLifecycleStatus = 'draft' | 'applied' | 'archived';

/** Logic component の表示用 glyph 種別 (LogicComponentCard の見た目を決める)。 */
export type LogicComponentGlyphKind = 'branch' | 'tableLookup' | 'split' | 'rounding' | 'sum' | 'arithmetic' | 'lookup' | 'switch';

/** Payroll item の type — 給与明細上のロール。
 *  earning: 支給項目 / deduction: 控除項目。
 *  入力データ・マスタ・中間値・帳票出力等は未設定 (undefined)。 */
export type PayrollItemType = 'earning' | 'deduction';

/** Payroll item の category — 制定根拠。
 *  statutory: 法定（健保・年金・税等） / company: 会社独自（役職手当等）。 */
export type PayrollItemCategory = 'statutory' | 'company';

/** Payroll item の variability — 月次変動性。
 *  fixed: 月毎で変動しない / variable: 月毎で変動する。 */
export type PayrollItemVariability = 'fixed' | 'variable';

/** Payroll item の taxability — 課税対象性。
 *  taxable: 課税 / non-taxable: 非課税 /
 *  partially-taxable: 一部課税（限度額超過分のみ課税の通勤費等） /
 *  conditionally-taxable: 条件付き課税（条件で課税範囲が変わる）。 */
export type PayrollItemTaxability = 'taxable' | 'non-taxable' | 'partially-taxable' | 'conditionally-taxable';

/** Payroll engine の item 原子マスタ。
 *  「年俸額」「健康保険料」「標準報酬月額」のような payroll structure / unit から参照される atom。
 *  category / kind union は持たない (engineComponents 時代の遺物を排除)。 */
export interface PayrollItem {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  /** JSON.stringify された任意の設定。tablePreview もここに含める。 */
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** structure detail カードの badge 文字 (例: "標報", "健保", "年俸") */
  displayBadge?: string;
  /** structure detail カードの badge 種別ラベル (例: "整数", "DEDUCTION・STATUTORY・VARIABLE") */
  displayBadgeKind?: string;
  /** 管理用名称 (例: name = 「基本給」, managementName = 「管理職基本給」)。 */
  managementName?: string;
  /** 給与明細上の表示名 (`name` が制度上の正式名で、明細では別名で出すケース)。 */
  paySlipDisplayName?: string;
  /** 給与明細上のロール。入力データやマスタ等の非明細項目は undefined。 */
  type?: PayrollItemType;
  /** 制定根拠。`type` 未設定の item では undefined。 */
  category?: PayrollItemCategory;
  /** 月次変動性。`type` 未設定の item では undefined。 */
  variability?: PayrollItemVariability;
  /** 課税対象性。`type === "earning"` の item で意味を持つ。 */
  taxability?: PayrollItemTaxability;
  /** 遡及計算対象か（前月以前への確定後再計算の対象）。 */
  retroactive?: boolean;
  /** 給与明細上の表示順 (昇順)。明細項目でない item は undefined。 */
  paySlipOrder?: number;
  /** Numbering CSV に基づく 4 桁番号。CSV にマッチしない item は undefined。 */
  number?: number;
}

/** Payroll engine の logic component 原子マスタ。
 *  「料率テーブル Lookup」「労使折半」「端数処理」「× 12 月割」のような計算ロジック atom。 */
export interface PayrollLogicComponent {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadgeKind?: string;
  /** LogicComponentCard の glyph (枠の中の絵柄) を決める。 */
  glyphKind?: LogicComponentGlyphKind;
}

/** Payroll input snapshot のソース領域。給与計算基準日にどのコア DB から凍結するかを表す。 */
export type PayrollInputSnapshotSourceCategory = 'people' | 'attendances' | 'organization' | 'contracts' | 'benefits' | 'commuting-expenses';

/** スナップショットの基準タイミング（いつ時点のコア DB 値を凍結するか）。 */
export type PayrollInputSnapshotAnchorTiming = /** 給与計算基準日時点の値 */
'calculation-base-date'
/** 勤怠締め日時点の値 */ | 'period-close-date'
/** 支給日時点の値 */ | 'payment-date'
/** 対象期間の集計値 */ | 'period-range';

/** スナップショット項目の値型。表示フォーマットの決定に使う。 */
export type PayrollInputSnapshotValueType = 'amount' | 'number' | 'duration' | 'date' | 'text' | 'boolean' | 'code';

/** Payroll input snapshot の項目定義。
 *  コア DB のどのフィールドを、いつ時点で凍結して payroll engine の計算インプットにするかのカタログ。
 *  値そのもの（人ごとの実測値）は保持せず、取得元と凍結タイミングの定義のみを持つ。 */
export interface PayrollInputSnapshotField {
  id: string;
  /** 一覧の No. 表示に使う 4 桁番号。カテゴリごとに 1000 番台を割り当てる。 */
  number: number;
  sourceCategory: PayrollInputSnapshotSourceCategory;
  /** 項目名 (例: 「基本給」) */
  name: string;
  /** 計算式から参照するコード (例: `contract.base_salary`) */
  code: string;
  /** コア DB 上の取得元パス (例: 「雇用契約 › 報酬 › 基本給」) */
  sourcePath: string;
  valueType: PayrollInputSnapshotValueType;
  /** 表示単位 (例: 「円」「時間」「日」)。単位を持たない項目は undefined。 */
  unit?: string;
  anchorTiming: PayrollInputSnapshotAnchorTiming;
  /** 計算に必須か。欠落時は計算前アラートの対象になる。 */
  required: boolean;
  /** 一覧の並び順 (昇順)。 */
  sortOrder: number;
}

/** Attendance engine の item 原子マスタ。PayrollItem と同形。 */
export interface AttendanceItem {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadge?: string;
  displayBadgeKind?: string;
}

/** Attendance engine の logic component 原子マスタ。PayrollLogicComponent と同形。 */
export interface AttendanceLogicComponent {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  config: string;
  validFrom: string;
  validTo: string | null;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  displayBadgeKind?: string;
  glyphKind?: LogicComponentGlyphKind;
}
export interface AttendanceStructure {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  /** この structure が参照する Unit の id (順序つき)。Unit はカタログ側で独立に存在する。 */
  unitIds: string[];
  /** この structure が直接参照する AttendanceItem の id */
  itemIds: string[];
  /** この structure が直接参照する AttendanceLogicComponent の id */
  logicComponentIds: string[];
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface PayrollStructure {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  countryCode: string;
  /** この structure が参照する Unit の id (順序つき)。Unit はカタログ側で独立に存在する。 */
  unitIds: string[];
  /** この structure が直接参照する PayrollItem の id */
  itemIds: string[];
  /** この structure が直接参照する PayrollLogicComponent の id */
  logicComponentIds: string[];
  compositionStages?: string[][];
  /** PayrollLogicComponent の id (= payment cycle として扱う logic component) */
  paymentCycleId?: string;
  isDefault: boolean;
  lifecycleStatus: EngineLifecycleStatus;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Unit / UnitItem / UnitLogicComponent (catalog atoms, structure 非従属) ─────────

// 型は features/engine-shared/structure-detail/types.ts が所有。mocks 側は consumer。
export type { StructureCardBadgeTone, StructureCellRole, StructureTablePreview } from '@/features/engine-shared/structure-detail/types';

/** PayrollUnit はカタログ atom として独立に存在する。Structure は unitIds で参照する。 */
export interface PayrollUnit {
  id: string;
  name: string;
  description?: string;
  parentUnitId?: string;
  branchCondition?: string;
  branchLabel?: 'T' | 'F';
  order: number;
  /** サイドタブで上位カテゴリ見出しを差し込むためのキー。Structure 側で同じ key を持つ unit が連続する区間に subheader を表示する。未設定の Unit は subheader を出さない（後方互換）。 */
  categoryKey?: string;
  lifecycleStatus: EngineLifecycleStatus;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Numbering CSV に基づく単一番号。CSV と直接マッチしない unit は undefined。 */
  number?: number;
}

/** PayrollUnit の input / output に登場する item の join 行。
 *  表示用情報は itemId → PayrollItem を join して取得する。 */
export interface PayrollUnitItem {
  id: string;
  unitId: string;
  /** FK → payrollItems.id。branch label 用 unit item (True/False output) は省略可。 */
  itemId?: string;
  role: Exclude<StructureCellRole, 'process'>;
  order: number;
  /** subject/period 依存の mock 表示値 ("472,460" 等)。未指定なら空欄。 */
  valueOverride?: string;
  /** 分岐 unit の True/False output 用。itemId と排他。 */
  branchLabel?: 'T' | 'F';
  /** 分岐先 unit の名前 (例: "介護保険料計算")。 */
  branchTargetUnitName?: string;
}

/** PayrollUnit の process に登場する logic component の join 行。
 *  表示用情報は logicComponentId → PayrollLogicComponent を join して取得する。 */
export interface PayrollUnitLogicComponent {
  id: string;
  unitId: string;
  logicComponentId: string;
  order: number;
  valueOverride?: string;
  /** 算術ロジックの計算に用いる input item ID 群。
   *  例: subtract の場合は被減数と減数の itemId。
   *  expanded 表示時に「何を使った計算か」を可視化するための情報。 */
  inputItemIds?: string[];
}
export interface AttendanceUnit {
  id: string;
  name: string;
  description?: string;
  parentUnitId?: string;
  branchCondition?: string;
  branchLabel?: 'T' | 'F';
  order: number;
  lifecycleStatus: EngineLifecycleStatus;
  countryCode: string;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface AttendanceUnitItem {
  id: string;
  unitId: string;
  itemId?: string;
  role: Exclude<StructureCellRole, 'process'>;
  order: number;
  valueOverride?: string;
  branchLabel?: 'T' | 'F';
  branchTargetUnitName?: string;
}
export interface AttendanceUnitLogicComponent {
  id: string;
  unitId: string;
  logicComponentId: string;
  order: number;
  valueOverride?: string;
  /** 算術ロジックの計算に用いる input item ID 群。
   *  expanded 表示時に「何を使った計算か」を可視化するための情報。 */
  inputItemIds?: string[];
}

// ─── Core Domain ────────────────────────────────────────────

export type PayrollAttendanceMetricType = 'employee_count' | 'payroll_status' | 'attendance_rate' | 'overtime_hours' | 'alert_count' | 'processing_progress';
export type MetricTrend = 'up' | 'down' | 'stable';
export type OperationPackageType = 'payroll' | 'attendance' | 'payroll_attendance';
export type OperationPackageCategory = 'monthly_close' | 'year_end' | 'onboarding' | 'offboarding' | 'mid_year_adjustment' | 'bonus_processing';
export type OperationPackageStatus = 'template' | 'in_progress' | 'completed';
export interface PayrollAttendanceDashboardMetric {
  id: string;
  period: string;
  metricType: PayrollAttendanceMetricType;
  label: string;
  value: number;
  unit: string;
  trend: MetricTrend;
  trendPercent: number;
  organizationId: string;
  updatedAt: string;
}
export interface OperationPackage {
  id: string;
  name: string;
  nameEn: string;
  description: string;
  type: OperationPackageType;
  category: OperationPackageCategory;
  steps: string;
  estimatedDuration: string;
  assigneeId: string | null;
  status: OperationPackageStatus;
  organizationId: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}
export interface AttendanceOpPackage {
  id: string;
  groupIndex: number;
  sortOrder: number;
  inProgressCount: number;
}
export interface AttendanceOpPackagePattern {
  id: string;
  packageId: string;
  sortOrder: number;
  nameKey: string;
}
export interface AttendanceOpPackageInstanceItem {
  id: string;
  patternId: string;
  sortOrder: number;
  row: SchemaTableRow;
}
export interface PayrollOpPackage {
  id: string;
  groupIndex: number;
  sortOrder: number;
  inProgressCount: number;
}
export interface PayrollOpPackagePattern {
  id: string;
  packageId: string;
  sortOrder: number;
  nameKey: string;
}
export interface PayrollOpPackageInstanceItem {
  id: string;
  patternId: string;
  sortOrder: number;
  row: SchemaTableRow;
}

// ─── Attendance Rule & Policy ───────────────────────────────

export type AttendanceRulePolicyCategory = 'assignment' | 'attendance' | 'period';
export interface AttendanceRulePolicy {
  id: string;
  category: AttendanceRulePolicyCategory;
  name: string;
  description: string;
  mandatory: boolean;
  clauseTypes: string[];
  config: string;
  organizationId: string;
  updatedAt: string;
}

// ─── Payroll Alert ──────────────────────────────────────────

export type PayrollAlertCategory = 'calculation' | 'compliance' | 'payment' | 'deadline';
export type PayrollAlertType = 'calc_failed' | 'negative_net_pay' | 'deduction_exceeds_gross' | 'minimum_wage_violation' | 'overtime_rate_mismatch' | 'withholding_tax_anomaly' | 'missing_bank_account' | 'fb_data_mismatch' | 'payment_date_overdue' | 'closing_deadline_exceeded' | 'resident_tax_update_due' | 'year_end_adjustment_pending';
export interface PayrollAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  period: string;
  payGroup: string;
  category: PayrollAlertCategory;
  type: PayrollAlertType;
  severity: AlertSeverity;
  message: string;
  detectedAt: string;
  resolved: boolean;
  resolvedAt: string | null;
  referenceId: string | null;
  organizationId: string;
}

// ─── Employee Pay Slip Alerts (detail page) ─────────────────
// payroll alerts と同形だが、pay slip 詳細画面の alerts タブ専用に独立テーブルで保持する。
// 既存 payrollAlerts への絞り込みは行わず、employee 単位で seed する。

export type EmployeePaySlipAlertCategory = PayrollAlertCategory;
export type EmployeePaySlipAlertType = PayrollAlertType;
export interface EmployeePaySlipAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  department: string;
  period: string;
  payGroup: string;
  category: EmployeePaySlipAlertCategory;
  type: EmployeePaySlipAlertType;
  severity: AlertSeverity;
  message: string;
  detectedAt: string;
  resolved: boolean;
  resolvedAt: string | null;
  referenceId: string | null;
  organizationId: string;
}

// ─── Employee Clocking Record Alerts (detail page) ──────────
// attendance alerts と同形だが、clocking record 詳細画面の alerts タブ専用に独立テーブルで保持する。

// 基底 AttendanceAlertType(staff サマリ/給与で利用)に加え、clocking record
// 詳細のアラートタブ専用で追加種別を持つ。基底は変更しない（他機能への影響回避）。
export type EmployeeClockingRecordAlertType = AttendanceAlertType | 'late_arrival' | 'early_leave' | 'holiday_work';
export interface EmployeeClockingRecordAlert {
  id: string;
  employeeId: string;
  employeeName: string;
  type: EmployeeClockingRecordAlertType;
  severity: AlertSeverity;
  message: string;
  date: string;
  resolved: boolean;
  resolvedAt: string | null;
  organizationId: string;
  createdAt: string;
}

// ─── Employee Pay Slip / Clocking Record Files (detail page) ─
// device files の簡易版。folder type 単位でファイル一覧を保持する mock 専用テーブル。

export type EmployeePaySlipFileFolderType = 'payslip' | 'bonus' | 'yearEndAdjustment';
export type EmployeeClockingRecordFileFolderType = 'attendanceBook' | 'shiftTable' | 'leaveRequest';
export interface EmployeePaySlipDetailFile {
  id: string;
  employeeId: string;
  organizationId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  folderType: EmployeePaySlipFileFolderType;
  uploadedByName: string;
  uploadedAt: string;
  updatedAt: string;
}
export interface EmployeeClockingRecordFile {
  id: string;
  employeeId: string;
  organizationId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  folderType: EmployeeClockingRecordFileFolderType;
  uploadedByName: string;
  uploadedAt: string;
  updatedAt: string;
}

// ─── Payroll Overview Facts ─────────────────────────────────

export type PayrollOverviewOfficeKey = 'tokyo_hq' | 'sendai' | 'fukuoka' | 'osaka' | 'sapporo';
export interface PayrollOverviewFact {
  id: string;
  employeeId: string;
  period: string;
  organizationId: string;
  officeKey: PayrollOverviewOfficeKey;
  baseSalary: number;
  overtimePay: number;
  allowances: number;
  grossPay: number;
  incomeTax: number;
  residentTax: number;
  healthInsurance: number;
  pensionInsurance: number;
  employmentInsurance: number;
  otherDeductions: number;
  totalDeductions: number;
  netPay: number;
  employerSocialInsuranceBurden: number;
}

// ─── Attendance Operation Activity ──────────────────────────

export type AttendanceActivityCategory = 'clocking' | 'request' | 'alert' | 'policy';
export type AttendanceActivityType = 'clocking.created' | 'clocking.edited' | 'clocking.deleted' | 'clocking.approved' | 'clocking.rejected' | 'request.leaveSubmitted' | 'request.overtimeSubmitted' | 'request.holidayWorkSubmitted' | 'request.approved' | 'request.rejected' | 'request.cancelled' | 'alert.triggered' | 'alert.acknowledged' | 'alert.resolved' | 'policy.ruleUpdated' | 'policy.attendanceStructureAssigned' | 'shift.assigned' | 'attendance.monthlyClosed' | 'attendance.reopened';
export type ActivityActorKind = 'user' | 'system';
export type ActivityOriginKind = 'manual' | 'operation';
export interface AttendanceOperationActivity {
  id: string;
  correlationId: string;
  occurredAt: string;
  category: AttendanceActivityCategory;
  activityType: AttendanceActivityType;
  targetEmployeeId: string;
  targetEmployeeName: string;
  actorId: string | null;
  actorName: string | null;
  actorKind: ActivityActorKind;
  originKind: ActivityOriginKind;
  operationTaskName: string | null;
  operationPackageName: string | null;
  patternName: string | null;
  detail: string;
  payload: string;
  organizationId: string;
}

// ─── Individual Portal Mock (visual-only dashboard) ─────────

/** 打刻種別。休憩は1日に複数回起こりうるため出退勤と同じイベント列で扱う。 */
export type IndividualPortalPunchType = 'clockIn' | 'breakStart' | 'breakEnd' | 'clockOut';

/** 1 回の打刻イベント。記録順（時系列）に積まれる。 */
export interface IndividualPortalPunch {
  type: IndividualPortalPunchType;
  /** ISO datetime recorded at punch time. */
  at: string;
}
export interface IndividualPortalAttendance {
  id: string;
  /** ISO date string for the displayed day (e.g. "2025-03-28"). */
  date: string;
  /** When true, the "打刻漏れ" alert row is shown. */
  hasMissingPunch: boolean;
  /** 打刻イベントの記録列。記録された分だけ時系列で保持する。 */
  punches: IndividualPortalPunch[];
}
export interface IndividualPortalTask {
  id: string;
  name: string;
  /** ISO date string. */
  dueDate: string;
  completedSteps: number;
  totalSteps: number;
  /** Sort order for stable rendering. */
  sortOrder: number;
  /**
   * When set, clicking the task in the portal opens the sequence runner at
   * `/individual/portal/sequence/{sequenceInstanceId}`. Leave undefined for
   * static / non-interactive task rows.
   */
  sequenceInstanceId?: string;
  /**
   * 本人起点の手続きタスク（デモ）用の直接遷移先。設定時は sequenceInstanceId より優先し、
   * クリックで当該手続きフォームを開く。
   */
  to?: '/individual/portal/procedures/offboarding' | '/individual/portal/procedures/onboarding';
}

// ─── Individual Portal Sequence (AI-driven flow) ────────────

export type IndividualPortalSequenceKind = 'year_end_adjustment' | 'onboarding' | 'hire_event' | 'clock_correction' | 'shift_rostering';

/** Speaker identity rendered above a chat bubble when multiple parties are present. */
export interface SequenceChatIdentity {
  name: string;
  avatarUrl?: string;
  avatarFallback?: string;
}

/** A pre-baked chat history entry shown above the floating chat card. */
export interface SequenceChatHistoryItem {
  key: string;
  kind: 'ai_bubble' | 'user_summary_card' | 'user_file_card' | 'draft_link' | 'tool_call' | 'automap_link';
  /** AI bubble text — supports `<strong>` and `<br/>` placeholder tokens. */
  text?: string;
  /** Title for summary cards ("STEP 1 回答"). */
  title?: string;
  /** Rows for summary cards. */
  rows?: {
    label: string;
    value: string;
  }[];
  /** File entries for `user_file_card`. */
  files?: {
    name: string;
  }[];
  /** Optional speaker identity. Falls back to default by `kind` when omitted. */
  identity?: SequenceChatIdentity;
  /** ISO datetime string. Optional footer display below the bubble. */
  timestamp?: string;
  /**
   * Optional direction override. By default user_summary_card / user_file_card は outgoing、
   * ai_bubble は incoming に表示される。
   * 例: 他者から転送された情報を `user_summary_card` で表示するときは "incoming" を指定する。
   */
  direction?: 'incoming' | 'outgoing';
  /** Label for the inline link button on `draft_link` / `automap_link`. */
  linkLabel?: string;
  /**
   * `draft_link` で開く detail content の指定（複数 content を持つシーケンス用）。
   * 例: シフト編成は "shift-requests"（シフト希望）。未指定なら既定 content。
   */
  draftTargetKey?: string;
  /**
   * Tool name shown on `tool_call` chips (例: "Automap Ingester")。
   * 直近の pending tool_call item に対しては、reveal 前の thinking インジケーターが
   * "Calling ${toolName}..." と表示される。
   */
  toolName?: string;
}
export type SequenceAnswerSpec = {
  kind: 'yes_no';
  yesLabel: string;
  noLabel: string;
  /**
   * YES/NO の上に表示する補助情報 callout（例: 病院予約連絡先 / 招待メールの宛先・件名）。任意。
   * iconName は seed が指定する抽象識別子で、renderer 側で IconName に解決する。
   * rows が 1 件のときは prominent な大きい表示、複数件のときは label/value ペアを縦に並べる。
   */
  callout?: {
    iconName: string;
    rows: Array<{
      label: string;
      value: string;
    }>;
  };
} | {
  kind: 'single_choice';
  options: Array<{
    key: string;
    pill?: string;
    label: string;
    sub?: string;
  }>;
} | {
  kind: 'single_choice_with_specs';
  submitLabel: string;
  options: Array<{
    key: string;
    pill?: string;
    label: string;
    sub?: string;
    specs: {
      label: string;
      value: string;
    }[];
  }>;
} | {
  kind: 'multi_choice';
  defaultSelected?: string[];
  submitLabel: string;
  options: Array<{
    key: string;
    label: string;
    sub?: string;
  }>;
} | {
  kind: 'amount';
  label: string;
  placeholder: string;
  suffix: string;
  submitLabel: string;
  quickValues: {
    value: string;
    label: string;
  }[];
  format: 'yen';
} | {
  kind: 'address_form';
  submitLabel: string;
  fields: Array<{
    id: string;
    label?: string;
    placeholder: string;
    defaultValue?: string;
    kind: 'postal' | 'city' | 'street' | 'tel' | 'text' | 'email';
  }>;
} | {
  kind: 'text_input';
  label?: string;
  placeholder: string;
  defaultValue?: string;
  submitLabel: string;
  maxLength?: number;
  /** 任意質問の場合に「回答しない」ボタンを表示するためのラベル。未指定なら skip 不可。 */
  skipLabel?: string;
} | {
  kind: 'date_input';
  label?: string;
  defaultValue?: string;
  submitLabel: string;
  min?: string;
  max?: string;
} | {
  kind: 'field_confirm';
  rows: Array<{
    id: string;
    label: string;
    value: string;
  }>;
  yesLabel: string;
  noLabel: string;
} | {
  kind: 'upload';
  hint: string;
  skipOption?: {
    key: string;
    pill?: string;
    label: string;
    sub?: string;
  };
  manualOption?: {
    key: string;
    pill?: string;
    label: string;
    sub?: string;
  };
  acceptedMimeTypes: string[];
  maxSizeBytes: number;
} | {
  kind: 'automap_review';
  title: string;
  subtitle: string;
  /** Upload step whose previewDataUrl + file names are surfaced on the left pane. */
  uploadStepId: string;
  certificates: AutomapCertificate[];
  confirmLabel: string;
} | {
  kind: 'complete';
  ctaLabel: string;
};
export interface SequenceStepDefinition {
  id: string;
  order: number;
  /** Right-panel + sidebar label, e.g. "個人情報・扶養家族の確認". */
  navLabel: string;
  /** Floating chat card title. */
  title: string;
  /** Floating chat card subtitle. */
  subtitle?: string;
  /** Pre-baked AI history shown above the floating card on entry. */
  history: SequenceChatHistoryItem[];
  answer: SequenceAnswerSpec;
}
export interface SequenceStackingDoc {
  date: string;
  iconName: string;
  label: string;
  /**
   * Reference エントリをクリックしたときに開くパネル。
   * 指定すると label がボタンになり、対応するハンドラが呼ばれる。
   * - "automap": Automap Ingester パネル
   * - "detail": detail draft パネル（シフト編成の希望/週/日/月など）
   */
  action?: 'automap' | 'detail';
  /**
   * detail パネル内で開く対象（複数の detail content を持つシーケンス用）。
   * 例: シフト編成は "store-schedule"（店舗のシフト表）/ "shift-requests"（シフト希望）。
   * 未指定なら detail の既定 content を開く。
   */
  draftTargetKey?: string;
  /**
   * このエントリを表示し始める step id。指定された step に到達するまでは
   * stackingDocs から隠す。未指定なら最初から表示される。
   * 例: "ye-step-automap" を指定すると、抽出 step の chat やりとりが
   *     発生して以降のみレファレンスに現れる。
   */
  visibleAfterStepId?: string;
}
export type DraftBadgeTone = 'primary' | 'success' | 'warning' | 'neutral' | 'draft';
export type DraftDcBlock = {
  kind: 'field_group';
  title: string;
  cols: 1 | 2;
  fields: {
    label: string;
    value: string;
  }[];
} | {
  kind: 'deduction_table';
  title?: string;
  rows: {
    provider: string;
    type: string;
    period?: string;
    amount: string;
  }[];
} | {
  kind: 'section_divider';
  iconName?: string;
  label: string;
  rightBadge?: {
    text: string;
    tone: DraftBadgeTone;
  };
} | {
  kind: 'info_callout';
  iconName: string;
  text: string;
} | {
  kind: 'summary_table';
  title?: string;
  headerLabels: {
    type: string;
    details: string;
    amount: string;
  };
  rows: {
    type: string;
    provider: string;
    amount: string;
  }[];
  totalRowLabel: string;
  totalAmount: string;
} | {
  kind: 'doc_table';
  rows: {
    name: string;
    fileName: string;
    status: {
      label: string;
      tone: DraftBadgeTone;
    };
  }[];
};
export interface DraftDcContent {
  badge: string;
  title: string;
  subtitle: string;
  blocks: DraftDcBlock[];
}

/** 文書系シーケンス（年末調整 / 入社手続き 等）の detail draft。DC ブロック + プレーン版。 */
export interface SequenceDocumentDetailDraft {
  kind: Exclude<IndividualPortalSequenceKind, 'shift_rostering'>;
  draftDc: DraftDcContent;
  draftPlain: string;
}

/**
 * スタッフから提出された 1 名分のシフト希望（mock seed）。
 * detail view の「希望」タブで一覧し、店長が反映の判断に使う。
 */
export interface ShiftRequestDraft {
  staffId: string;
  staffName: string;
  position: ShiftPosition;
  /**
   * 締切後に提出された遅れ組。`requestsCompleteAtStepIndex` 到達まで未提出として扱い、
   * 「全員提出が揃う」演出に使う。
   */
  submittedLate?: boolean;
  /** 希望スロット。`kind` で勤務希望 / 休み希望 / 時間限定を区別する。 */
  entries: Array<{
    /** "YYYY-MM-DD"。 */
    date: string;
    startH: number;
    endH: number;
    kind: 'preferred' | 'off' | 'limited';
    note?: string;
  }>;
}

/**
 * 店長のシフト編成 detail view。希望 / 週 / 日 / 月 を切替表示する。
 * 週/日/月グリッドは Dexie の店舗シフト実データ（getStoreShiftDetail）を読み、
 * 希望タブは seed された `requests` を表示する。
 */
export interface ShiftScheduleDetailDraft {
  kind: 'shift_rostering';
  /** 表示対象の店舗（pharmacyStores）。 */
  storeId: string;
  /** 対象月 "YYYY-MM"（週/日/月グリッドの読み取り範囲）。 */
  monthPrefix: string;
  /** 詳細の初期アンカー日 "YYYY-MM-DD"。 */
  anchorDate: string;
  /** スタッフから提出されたシフト希望。 */
  requests: ShiftRequestDraft[];
  /**
   * シフト希望が「提出済み」として表示され始める step index（0 始まり）。
   * 店長が希望提出を依頼する step を完了し、`currentStepIndex` がこの値以上に
   * なるまでは、希望タブは「依頼前」の空状態を表示する（事前提出に見えないようにする）。
   */
  requestsReleasedAtStepIndex: number;
  /**
   * すべてのシフト希望が出揃う step index（0 始まり）。これ未満の間は `submittedLate`
   * のメンバーを未提出として扱う。この値以上で全員提出が揃い、自動生成が可能になる。
   */
  requestsCompleteAtStepIndex: number;
}
export type SequenceDetailDraft = SequenceDocumentDetailDraft | ShiftScheduleDetailDraft;

/**
 * AI mock sequence のサイド。
 * - "individual": 従業員（被管理者）が起点となるフロー（従来から存在）
 * - "cockpit": 人事労務担当者（管理者）が後続業務を実行するフロー
 *
 * `IndividualPortal*` という型名は歴史的経緯（individual 側から先に実装された）。
 * 現在は両サイドの sequence / chat session を単一の Dexie テーブルで保持している。
 */
export type AiMockSide = 'individual' | 'cockpit';

/** v5 以前のレコードは `side` を持たないため、既定値 "individual" として扱う。 */
export function rowSide(row: {
  side?: AiMockSide;
}): AiMockSide {
  return row.side ?? 'individual';
}
export interface IndividualPortalSequenceDefinition {
  id: string;
  kind: IndividualPortalSequenceKind;
  /** どちらのサイドのフローか。未指定の場合は "individual" として扱う。 */
  side?: AiMockSide;
  packageTitle: string;
  shortName: string;
  estimatedMinutes: number;
  steps: SequenceStepDefinition[];
  stackingDocs: SequenceStackingDoc[];
  detailDraft: SequenceDetailDraft;
  /** 詳細パネル下部の確定ボタンのラベル。未指定なら「申請する」。業務処理系では「確定する」など。 */
  submitLabel?: string;
  /** 「申請する」押下後に staged reveal で流す履歴。完了確認 + 受理メッセージ。 */
  completionHistory?: SequenceChatHistoryItem[];
}
export type IndividualPortalSequenceInstanceStatus = 'not_started' | 'in_progress' | 'completed';
export type IndividualPortalSequenceRightPanelMode = 'cards' | 'detail' | 'automap';
export interface IndividualPortalSequenceInstance {
  id: string;
  definitionId: string;
  taskId: string;
  status: IndividualPortalSequenceInstanceStatus;
  currentStepId: string;
  rightPanelMode: IndividualPortalSequenceRightPanelMode;
  sidebarOpen: boolean;
  startedAt: string;
  updatedAt: string;
  completedAt: string | null;
}
export type SequenceUploadedFile = {
  fileName: string;
  sizeBytes: number;
  mimeType: string;
  receivedAt: string;
  /**
   * Image-only inline preview. Populated by the mock upload API via
   * FileReader.readAsDataURL so the Automap review can render the original
   * source after reload. Absent for PDFs and other non-image uploads.
   */
  previewDataUrl?: string;
  /**
   * Runtime-only blob URL (created via URL.createObjectURL).
   * Used to preview real uploaded files (PDF iframe etc.) in the Automap
   * Ingester review screen. Not persisted across reloads.
   */
  objectUrl?: string;
};
export type AutomapFieldConfidence = 'high' | 'medium' | 'low';
export interface AutomapField {
  id: string;
  label: string;
  defaultValue: string;
  kind: 'text' | 'amount';
  confidence: AutomapFieldConfidence;
}
export interface AutomapCertificate {
  id: string;
  documentLabel: string;
  fields: AutomapField[];
}
export type SequenceStepRecordAnswer = {
  kind: 'yes_no';
  value: 'yes' | 'no';
} | {
  kind: 'single_choice';
  selectedKey: string;
} | {
  kind: 'single_choice_with_specs';
  selectedKey: string;
} | {
  kind: 'multi_choice';
  selectedKeys: string[];
} | {
  kind: 'amount';
  value: string;
} | {
  kind: 'address_form';
  values: Record<string, string>;
} | {
  kind: 'field_confirm';
  value: 'yes' | 'no';
} | {
  kind: 'text_input';
  value: string;
} | {
  kind: 'date_input';
  value: string;
} | {
  kind: 'upload';
  files: SequenceUploadedFile[];
  skipped?: boolean;
} | {
  kind: 'automap_review';
  values: Array<{
    certificateId: string;
    fields: Array<{
      id: string;
      value: string;
    }>;
  }>;
} | {
  kind: 'complete';
};
export interface IndividualPortalSequenceStepRecord {
  /** Composite: `${instanceId}:${stepId}` */
  id: string;
  instanceId: string;
  stepId: string;
  status: 'pending' | 'in_progress' | 'completed';
  completedAt: string | null;
  answer: SequenceStepRecordAnswer | null;
}
export type IndividualPortalApprovalStatus = 'pending' | 'in_progress' | 'completed' | 'rejected';
export type IndividualPortalApprovalCategory = 'labor' | 'it' | 'attendance' | 'ga' | 'contract';
export interface IndividualPortalApprovalFlowStep {
  order: number;
  approverName: string;
  approverAvatarColor: string;
  status: 'completed' | 'current' | 'pending';
  /** Set on the step that the current viewer is responsible for. */
  isMe?: boolean;
}
export interface IndividualPortalApprovalFormField {
  label: string;
  value: string;
}
export interface IndividualPortalApproval {
  id: string;
  taskName: string;
  /** ISO date string. */
  approvalDeadline: string;
  /** ISO date string. */
  submittedAt: string;
  requesterName: string;
  /** Avatar accent color used for the round chip behind the requester name. */
  requesterAvatarColor: string;
  approvalFlowLabel: string;
  status: IndividualPortalApprovalStatus;
  category: IndividualPortalApprovalCategory;
  /** Form key/value summary shown in the drawer body. */
  formSummary: IndividualPortalApprovalFormField[];
  /** Approval-flow steps shown in the drawer body. */
  approvalFlow: IndividualPortalApprovalFlowStep[];
  sortOrder: number;
}
export type IndividualPortalRequestApp = 'zoom' | 'zendesk';

// 'submitted' = 申請直後（本人起点の届出が提出され、労務の確認待ち）
export type IndividualPortalRequestStatus = 'in_progress' | 'rejected' | 'submitted';
export interface IndividualPortalRequest {
  id: string;
  name: string;
  /** IT アプリ申請のときだけ設定。労務系の届出（退職申請等）では未設定。 */
  app?: IndividualPortalRequestApp;
  /** ISO date string. */
  submittedAt: string;
  currentStep: number;
  totalSteps: number;
  status: IndividualPortalRequestStatus;
  sortOrder: number;
}
export type IndividualPortalRequestFormCategory = 'attendance' | 'it' | 'labor' | 'contract';
export interface IndividualPortalRequestForm {
  id: string;
  name: string;
  category: IndividualPortalRequestFormCategory;
  sortOrder: number;
  /**
   * BE の OperationPackageType に対応。値が設定されている手続きは Individual から申請可能で、
   * /v2/self-service/operation-packages の packageType と一致したパッケージへ遷移できる。
   */
  packageType?: string;
  /**
   * 本人起点のモック手続きフォームへ直接遷移させるデモ導線用のルート。
   * packageType 経路ではなく指定ルートへ Link する（デモ用）。
   */
  to?: '/individual/portal/procedures/offboarding' | '/individual/portal/procedures/onboarding';
}
export interface IndividualPortalAiChatSession {
  id: string;
  title: string;
  subtitle: string;
  /** ISO datetime. */
  lastInteractedAt: string;
  /** Linked sequence instance id. `null` for non-clickable items. */
  sequenceInstanceId: string | null;
  sortOrder: number;
  /** どちらのサイドのセッションか。未指定の場合は "individual"。 */
  side?: AiMockSide;
}
export interface IndividualPortalUpcomingInteraction {
  id: string;
  title: string;
  subtitle: string;
  /** ISO date. */
  scheduledAt: string;
  sequenceInstanceId: string | null;
  sortOrder: number;
  /** どちらのサイドの予定か。未指定の場合は "individual"。 */
  side?: AiMockSide;
}

// ─── Global AI Chat Mock ─────────────────────────────────────
// セッション・メッセージは BE（oRPC chatContract）が SSOT のためモック型を持たない。
// モデル選択のみ BE に概念が無い FE 専用機能としてモックする。

// ─── UI Preferences (frontend-only, key-value) ──────────────

export interface UiPreference {
  key: string;
  value: unknown;
}

// ─── Pay slip PDF mock (single common blob for "PDFでみる" button) ─

export interface PaySlipPdfMock {
  id: string;
  blob: Blob;
}

// ─── Pharmacy Shift Domain (mock) ───────────────────────────
//
// 薬局/ドラッグストアのシフト管理デモ用。一覧の行単位は「拠点(店舗)」または
// 「組織ユニット(エリア)」で切り替える。シフト・スタッフ・アラートを Dexie で保持する。

/** シフト一覧の行単位。place=店舗（店長視点）/ orgUnit=エリア（俯瞰視点）。 */
export type ShiftUnitType = 'place' | 'orgUnit';

/** 薬局の職種。無資格販売リスク判定では pharmacist / registeredSeller が有資格。 */
export type ShiftPosition = 'pharmacist' | 'registeredSeller' | 'cashier' | 'cosmetics' | 'stocker';
export type ShiftEmploymentType = 'full_time' | 'part_time';

/** 組織ユニット = エリア（関東 / 関西 …）。複数店舗を束ねる俯瞰単位。 */
export interface PharmacyArea {
  id: string;
  name: string;
  sortOrder: number;
}

/** 拠点 = 店舗（渋谷店 / 新宿店 …）。 */
export interface PharmacyStore {
  id: string;
  name: string;
  code: string;
  areaId: string;
  /** 営業時間（24h 表記）。無資格販売リスク（有資格者不在）の判定窓。 */
  openHour: number;
  closeHour: number;
  /** 月次の人件費予算（円）。人件費予算超過アラートの基準。 */
  monthlyLaborBudget: number;
  /** 各時間帯に最低限必要な人員数（シフト未充足判定の基準）。 */
  requiredHeadcount: number;
  sortOrder: number;
}
export interface ShiftStaff {
  id: string;
  storeId: string;
  name: string;
  initials: string;
  position: ShiftPosition;
  /** 薬剤師 / 登録販売者の資格保有（無資格販売リスク判定に使用）。 */
  qualified: boolean;
  employmentType: ShiftEmploymentType;
  /** 時給（円）。人件費の算出に使用。 */
  hourlyWage: number;
}
export interface ShiftAssignment {
  id: string;
  storeId: string;
  staffId: string;
  /** YYYY-MM-DD */
  date: string;
  startH: number;
  endH: number;
  breakStartH: number | null;
  breakEndH: number | null;
  position: ShiftPosition;
  note: string;
}
export type ShiftAlertType = 'unqualified_sales_risk' | 'understaffed' | 'labor_cost_over' | 'overtime_over';
export type ShiftAlertStatus = 'open' | 'requested' | 'resolved';
export type ShiftAlertSeverity = 'warning' | 'critical';
export interface ShiftAlert {
  id: string;
  storeId: string;
  storeName: string;
  type: ShiftAlertType;
  severity: ShiftAlertSeverity;
  /** YYYY-MM-DD（対象日）。 */
  date: string;
  /** 時間帯（無資格販売リスク / 未充足のみ。それ以外は null）。 */
  startH: number | null;
  endH: number | null;
  /** 対象職種（任意）。 */
  position: ShiftPosition | null;
  /** 表示用メッセージ（mock の固定文言。AttendanceAlert.message と同様にデータとして保持）。 */
  message: string;
  status: ShiftAlertStatus;
  /** 応援要請を起票した場合の coverage row id（縦串連携）。 */
  linkedCoverageRequestId: string | null;
  createdAt: string;
}

// ─── 個人ポータル 照会リソース（拠点 / メンバー資格 / デバイス / アカウント / 備品） ─
// 「権限モデル検討 1st」モックの参考デザインに対応する read-only リソース群。
// 操作者本人 = portalPersons の isMe=true（田中 美咲）。

/** 照会リソースの保有者（本人 + 関係者）。device / equipment / certification / account から参照。 */
export interface PortalPerson {
  id: string;
  name: string;
  /** アバター表示用の頭文字。 */
  initials: string;
  email: string;
  /** 所属（部署）。 */
  org: string;
  /** 操作者本人なら true。 */
  isMe: boolean;
}

/** 拠点。本人が責任者として管掌する拠点は managedByMe=true。 */
export interface PortalPlace {
  id: string;
  name: string;
  address: string;
  /** 責任者（portalPersons.id）。未設定なら null。 */
  managerPersonId: string | null;
  /** 操作者本人が管掌する拠点。 */
  managedByMe: boolean;
  sortOrder: number;
}

/** デバイス / 備品の保有形態。assigned=個人割当 / placed=拠点配置。 */
export type PortalAssetStatus = 'assigned' | 'placed';

/** デバイス。 */
export interface MemberDevice {
  id: string;
  name: string;
  /** 種別（ノートPC / タブレット …）。表示はそのまま使用（mock データ）。 */
  category: string;
  assetTag: string;
  status: PortalAssetStatus;
  /** 割当先（個人）。拠点配置なら null。 */
  holderPersonId: string | null;
  /** 設置拠点（拠点に据え置く機器）。 */
  installPlaceId: string | null;
  /** 保管拠点。 */
  storagePlaceId: string | null;
  serialNumber: string;
  os: string;
  /** YYYY-MM-DD */
  purchasedAt: string;
  sortOrder: number;
}

/** 備品。 */
export interface MemberEquipment {
  id: string;
  name: string;
  category: string;
  assetTag: string;
  status: PortalAssetStatus;
  holderPersonId: string | null;
  placeId: string | null;
  /** YYYY-MM-DD */
  purchasedAt: string;
  note: string;
  sortOrder: number;
}

/** メンバー資格。 */
export interface MemberCertification {
  id: string;
  name: string;
  holderPersonId: string;
  /** 認証番号。 */
  number: string;
  /** 発行団体。 */
  issuer: string;
  /** YYYY-MM-DD。未設定なら ''。 */
  lastUpdated: string;
  /** 有効期限。無期限なら null。 */
  expireAt: string | null;
  /** 更新期限。なければ null。 */
  renewBy: string | null;
  note: string;
  sortOrder: number;
}

/** ソフトウェアアカウント。 */
export interface MemberAccount {
  id: string;
  app: string;
  /** ソフトウェアのカテゴリ（種別内訳の集計軸）。 */
  category: string;
  account: string;
  displayName: string;
  role: string;
  /** YYYY-MM-DD */
  createdAt: string;
  /** YYYY-MM-DD */
  lastSignIn: string;
  mfaEnabled: boolean;
  holderPersonId: string;
  sortOrder: number;
}

// ─── 職歴情報（Work Experience）タブ mock（メンバー詳細） ─────

/**
 * 職歴情報タブのローカライズ済み文字列。
 * デモデータは ja / en の2言語のみ保持する（UI ラベルは共有 i18n の6言語対応）。
 */
export interface WorkExperienceLocalizedText {
  ja: string;
  en: string;
}

/** 職歴のポジション（会社配下の子アイテム）。日付は 'YYYY.MM'、在職中は 'CURRENT'。 */
export interface WorkExperiencePosition {
  id: string;
  title: WorkExperienceLocalizedText;
  employment: WorkExperienceLocalizedText;
  start: string;
  end: string;
  grade: WorkExperienceLocalizedText | null;
  department: WorkExperienceLocalizedText;
  responsibilities: WorkExperienceLocalizedText;
}

/** 職歴の会社（親アイテム）。positions は新しい順。 */
export interface WorkExperienceCompany {
  id: string;
  name: WorkExperienceLocalizedText;
  location: WorkExperienceLocalizedText;
  start: string;
  end: string;
  isCurrent: boolean;
  positions: WorkExperiencePosition[];
}

// ─── Device License（デバイス詳細「ライセンス」タブ用の見せ球データ） ───
// 実 BE にデバイス起点でライセンスを逆引きする API が無いためのデモ用擬似データ。
// 実 API 実装時はこのテーブルと seed を削除し、ルートの -api を差し替える。

export interface DeviceLicense {
  id: string;
  /** 将来: 特定デバイスへの紐付け。現状は null=どのデバイスでも共通表示 */
  deviceId: string | null;
  /** ソフトウェア名（インストール型ソフトウェア） */
  softwareName: string;
  /** 製品バージョン */
  productVersion: string;
  /** ライセンス名（プラン・エディション） */
  licenseName: string;
  sortOrder: number;
}

// ─── License Violation Alert（ライセンス配下の違反集約アラート用の見せ球） ───
// 全ライセンス横断の割当違反アラートを集約表示するデモ用擬似データ。
// 実 BE には横断集約 API が無いため（VIOLATED は USER_BASED 単位のみ）、モック DB で保持する。
// 実 API 実装時はこのテーブルと seed を削除し、ルートの -query を差し替える。

export type LicenseViolationCategory = 'NO_LICENSE' | 'VIOLATED' | 'ALUMNI';
export interface LicenseViolationAlert {
  id: string;
  /** 要付与=NO_LICENSE / 要剥奪=VIOLATED / 離籍者=ALUMNI */
  category: LicenseViolationCategory;
  /** 対象ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 対象メンバー（利用者名。該当なしは空文字） */
  targetMember: string;
  /** 対象デバイス（デバイス名。該当なしは空文字） */
  targetDevice: string;
  sortOrder: number;
}

// ─── License 配下タブの見せ球（Overview / メンバー / オペレーションアクティビティ / ルール&ポリシー） ───
// 実 BE の対応 API が未整備のためモック DB で保持する。
// 実 API 実装時はこれらのテーブルと seed を削除し、各ルートの -api を差し替える。

/** メンバータブ：ライセンス付与メンバー */
export interface LicenseMember {
  id: string;
  /** メンバー名 */
  name: string;
  /** 部署 */
  department: string;
  /** 付与されているライセンス */
  licenses: string[];
  sortOrder: number;
}

/** オペレーションアクティビティタブ：ライセンス操作履歴 */
export interface LicenseActivity {
  id: string;
  /** 発生日時 */
  occurredAt: string;
  /** 操作種別 */
  activityTypeLabel: string;
  /** ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 実行者 */
  actorName: string;
  sortOrder: number;
}

/** ルール&ポリシータブ：ライセンス割当ルール（メンバー割当 or デバイス割当の排他） */
export interface LicenseAssignmentRule {
  id: string;
  /** ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 対象メンバーグループ（対象なしは空文字） */
  memberGroup: string;
  /** 対象デバイスグループ（対象なしは空文字） */
  deviceGroup: string;
  sortOrder: number;
}

/** Overview：ソフトウェア別のシート保有/利用状況 */
export interface LicenseSeat {
  id: string;
  /** ソフトウェア名 */
  software: string;
  /** 保有シート数 */
  total: number;
  /** 利用中シート数 */
  assigned: number;
  /** 保有シート数の前月比 */
  totalChange: number;
  /** 利用中シート数の前月比 */
  assignedChange: number;
  sortOrder: number;
}

/** Overview：ライセンス更新予定 */
export interface LicenseRenewal {
  id: string;
  /** ソフトウェア名 */
  softwareName: string;
  /** ライセンス名 */
  licenseName: string;
  /** 更新日（YYYY-MM-DD） */
  renewalDate: string;
  /** シート数 */
  seats: number;
  sortOrder: number;
}

// ─── Re-export gantt snapshot row from operationEventPayload ─

export type { OperationEventSnapshotRow };